import { DateTime } from 'luxon';

type AutoPopulatedStartDateTime = {
  startDate: string;
  startTime: string;
  sessionDateTime: Date | null;
};

type StartDateTimeUpdate = Partial<{
  startDate: string;
  startTime: string;
  sessionDateTime: Date | null;
}>;

export const mergeStartDateTimeValues = (
  state: GroupSectionFormData,
  autoPopulated: AutoPopulatedStartDateTime,
  latestEditedStartTime: string,
): AutoPopulatedStartDateTime => ({
  startDate: state.startDate || autoPopulated.startDate,
  startTime:
    latestEditedStartTime || state.startTime || autoPopulated.startTime,
  sessionDateTime: state.sessionDateTime || autoPopulated.sessionDateTime,
});

export const applyAutoPopulatedStartDateTimeUpdates = (
  prevAuto: AutoPopulatedStartDateTime,
  updates: StartDateTimeUpdate,
): AutoPopulatedStartDateTime => ({
  startDate:
    typeof updates.startDate === 'string'
      ? updates.startDate
      : prevAuto.startDate,
  startTime:
    typeof updates.startTime === 'string'
      ? updates.startTime
      : prevAuto.startTime,
  sessionDateTime:
    'sessionDateTime' in updates && updates.sessionDateTime !== undefined
      ? updates.sessionDateTime
      : prevAuto.sessionDateTime,
});

export const shouldApplyFrequencyFieldUpdate = (
  field: keyof GroupSectionFormData,
  value: GroupSectionFormData[keyof GroupSectionFormData],
  isFieldDisabled: boolean,
): boolean => {
  return field === 'frequency' && typeof value === 'string' && !isFieldDisabled;
};

export const shouldApplyDayOfWeekFieldUpdate = (
  field: keyof GroupSectionFormData,
  value: GroupSectionFormData[keyof GroupSectionFormData],
): boolean => {
  return field === 'dayOfWeek' && typeof value === 'string' && !!value;
};

export const shouldApplyStartDateFieldUpdate = (
  field: keyof GroupSectionFormData,
  value: GroupSectionFormData[keyof GroupSectionFormData],
): boolean => {
  return field === 'startDate' && typeof value === 'string' && !!value;
};

export const shouldApplyTimezoneFieldUpdate = (
  field: keyof GroupSectionFormData,
  value: GroupSectionFormData[keyof GroupSectionFormData],
): boolean => {
  return field === 'timezone' && typeof value === 'string';
};

export const shouldValidateDayOfWeek = (
  hasUserEditedStartDate: boolean,
  isEditMode: boolean,
  showDate: boolean | undefined,
): boolean => {
  return hasUserEditedStartDate || (isEditMode && !!showDate);
};

export const calculateSessionDateTime = (
  startDate: string,
  startTime: string,
  timezone: string,
): Date => {
  return DateTime.fromFormat(
    `${startDate} ${startTime}`,
    'yyyy-MM-dd HH:mm:ss',
    { zone: timezone },
  ).toJSDate();
};

export const calculateSessionDateTimeFromTime = (
  startTime: string,
  timezone: string,
): Date => {
  return DateTime.fromFormat(startTime, 'HH:mm:ss', {
    zone: timezone,
  }).toJSDate();
};

export const getFrequencyChangeUpdates = ({
  frequency,
  prev,
  autoPopulated,
  latestEditedStartTime,
  DAY_OF_WEEK_DISABLED_FREQUENCIES,
  getDayOfWeekFieldUpdates,
}: {
  frequency: string;
  prev: GroupSectionFormData;
  autoPopulated: AutoPopulatedStartDateTime;
  latestEditedStartTime: string;
  DAY_OF_WEEK_DISABLED_FREQUENCIES: Set<string>;
  getDayOfWeekFieldUpdates: (
    dayOfWeek: string,
    formData: GroupSectionFormData,
  ) => Partial<GroupSectionFormData>;
}): {
  dayOfWeek: string;
  autoPopulatedUpdates?: StartDateTimeUpdate;
} => {
  const mergedStartDateTime = mergeStartDateTimeValues(
    prev,
    autoPopulated,
    latestEditedStartTime,
  );

  if (DAY_OF_WEEK_DISABLED_FREQUENCIES.has(frequency)) {
    const nowInTimezone = DateTime.now().setZone(prev.timezone);
    const todayDate = nowInTimezone.toFormat('yyyy-MM-dd');
    const timeToUse =
      mergedStartDateTime.startTime || nowInTimezone.toFormat('HH:mm:ss');
    const nextSessionDateTime = DateTime.fromFormat(
      `${todayDate} ${timeToUse}`,
      'yyyy-MM-dd HH:mm:ss',
      { zone: prev.timezone },
    ).toJSDate();

    return {
      dayOfWeek: '',
      autoPopulatedUpdates: {
        startDate: todayDate,
        startTime: timeToUse,
        sessionDateTime: nextSessionDateTime,
      },
    };
  }

  if (!prev.dayOfWeek) {
    return { dayOfWeek: '' };
  }

  const dayOfWeekUpdates = getDayOfWeekFieldUpdates(prev.dayOfWeek, {
    ...prev,
    startTime: mergedStartDateTime.startTime,
  });

  return {
    dayOfWeek: '',
    autoPopulatedUpdates: {
      startDate:
        typeof dayOfWeekUpdates.startDate === 'string'
          ? dayOfWeekUpdates.startDate
          : undefined,
      startTime:
        typeof dayOfWeekUpdates.startTime === 'string'
          ? dayOfWeekUpdates.startTime
          : undefined,
      sessionDateTime:
        'sessionDateTime' in dayOfWeekUpdates
          ? (dayOfWeekUpdates.sessionDateTime as Date) || null
          : undefined,
    },
  };
};

export const calculateStartTimeUpdate = (
  timeStr: string,
  startDate: string | undefined,
  timezone: string,
): {
  startTime: string;
  sessionDateTime: Date;
} => {
  let sessionDt: DateTime;

  if (startDate) {
    sessionDt = DateTime.fromFormat(
      `${startDate} ${timeStr}`,
      'yyyy-MM-dd HH:mm:ss',
      { zone: timezone },
    );
  } else {
    sessionDt = DateTime.fromFormat(timeStr, 'HH:mm:ss', {
      zone: timezone,
    });
  }

  return {
    startTime: timeStr,
    sessionDateTime: sessionDt.toJSDate(),
  };
};

export const prepareFormValue = <T>(value: T): T => {
  return Array.isArray(value) ? ([...value] as T) : value;
};

export const shouldClearDayOfWeekError = (
  field: keyof GroupSectionFormData,
): boolean => {
  return field === 'frequency' || field === 'dayOfWeek';
};

type RequestDataParams = {
  groupId: string;
  isEditMode: boolean;
  groupSectionId?: string;
  formState: GroupSectionFormData;
  mergedStartDateTime: {
    startDate: string;
    startTime: string;
    sessionDateTime: Date | null;
  };
  effectiveEndDate: string;
  hasUserEditedStartDate: boolean;
  hasUserEditedMaxAttendees: boolean;
  formatDateTimeInTimezone: (
    date: string,
    time: string,
    timezone: string,
  ) => string;
};

export const prepareSubmitRequestData = ({
  groupId,
  isEditMode,
  groupSectionId,
  formState,
  mergedStartDateTime,
  effectiveEndDate,
  hasUserEditedStartDate,
  hasUserEditedMaxAttendees,
  formatDateTimeInTimezone,
}: RequestDataParams) => {
  const {
    hosts,
    frequency,
    duration,
    length,
    maximumAttendees,
    mustAttendAll,
    isWebinar,
    timezone,
  } = formState;

  return {
    groupId,
    ...(isEditMode && groupSectionId ? { sectionId: groupSectionId } : {}),
    hostUserIds: hosts.map((host) => host.value),
    frequency,
    duration: Number(duration),
    startDate: formatDateTimeInTimezone(
      mergedStartDateTime.startDate,
      mergedStartDateTime.startTime,
      timezone,
    ),
    endDate: effectiveEndDate
      ? formatDateTimeInTimezone(
          effectiveEndDate,
          mergedStartDateTime.startTime,
          timezone,
        )
      : null,
    length: length || undefined,
    maximumAttendees: maximumAttendees || undefined,
    showDate:
      isEditMode && !hasUserEditedStartDate
        ? (formState.showDate ?? true)
        : !!formState.startDate,
    mustAttendAll,
    isWebinar,
    showMaximumAttendees: hasUserEditedMaxAttendees,
  };
};

export const computeShouldValidateDayOfWeek = (
  hasUserEditedStartDate: boolean,
  isEditMode: boolean,
  showDate: boolean | undefined,
): boolean => {
  return hasUserEditedStartDate || (isEditMode && !!showDate);
};
